import { useEffect, useState } from "react";
import api from "../lib/api";
import { KanbanBoard } from "../components/KanbanBoard";
import { DataPorter } from "../components/DataPorter";
import { FunnelList, ViewToggle } from "../components/FunnelList";
import { DOCTOR_STAGES, INDIA_CITIES, inr, labelOf } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Building2, Stethoscope, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const SPECIALTIES = ["Orthopedics", "Neurology", "Sports Medicine", "Rheumatology", "General Physician", "Physical Medicine", "Geriatrics", "Spine Surgery"];
const NMC = { comfortable: "bg-emerald-100 text-emerald-700", neutral: "bg-slate-100 text-slate-600", concerned: "bg-amber-100 text-amber-700", unknown: "bg-slate-100 text-slate-500" };

const empty = { name: "", specialty: "Orthopedics", clinic: "", city: "Chennai", patient_volume_estimate: 0, monthly_order_potential: 0, est_patients_per_month: 0, est_revenue_per_month: 0, relationship_owner: "", nmc_comfort: "unknown", notes: "" };

export default function DoctorsFunnel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list");
  const [form, setForm] = useState(empty);
  const [detail, setDetail] = useState(null);
  const [docTarget, setDocTarget] = useState(160);
  const [edit, setEdit] = useState(null);

  const hydrate = (d) => setEdit({
    name: d.name || "", specialty: d.specialty || "", clinic: d.clinic || "", city: d.city || "",
    patient_volume_estimate: d.patient_volume_estimate ?? 0, monthly_order_potential: d.monthly_order_potential ?? 0,
    est_patients_per_month: d.est_patients_per_month ?? 0, est_revenue_per_month: d.est_revenue_per_month ?? 0,
    relationship_owner: d.relationship_owner || "", nmc_comfort: d.nmc_comfort || "unknown",
    stage: d.stage || "prospect_identified",
    last_contact_date: (d.last_contact_date || "").slice(0, 10),
    onboarded_date: (d.onboarded_date || "").slice(0, 10),
    objections: (d.objections || []).join(", "),
    notes: d.notes || "",
  });
  const setField = (k, v) => setEdit((p) => ({ ...p, [k]: v }));

  const load = () => api.get("/doctors").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/settings/targets").then((r) => setDocTarget(r.data.doctor_target));
  }, []);

  const move = async (item, stage) => {
    await api.patch(`/doctors/${item._id}/stage`, { stage });
    toast.success(`Moved to ${labelOf(DOCTOR_STAGES, stage)}`);
    load();
  };

  const create = async () => {
    if (!form.name) return toast.error("Name required");
    await api.post("/doctors", { ...form, patient_volume_estimate: Number(form.patient_volume_estimate), monthly_order_potential: Number(form.monthly_order_potential), est_patients_per_month: Number(form.est_patients_per_month), est_revenue_per_month: Number(form.est_revenue_per_month) });
    toast.success("Doctor added");
    setOpen(false); setForm(empty); load();
  };

  const openDetail = async (item) => {
    const { data } = await api.get(`/doctors/${item._id}`);
    setDetail(data); hydrate(data);
  };

  const saveEdit = async () => {
    if (!edit.name) return toast.error("Name required");
    const payload = {
      ...edit,
      patient_volume_estimate: Number(edit.patient_volume_estimate) || 0,
      monthly_order_potential: Number(edit.monthly_order_potential) || 0,
      est_patients_per_month: Number(edit.est_patients_per_month) || 0,
      est_revenue_per_month: Number(edit.est_revenue_per_month) || 0,
      objections: edit.objections ? edit.objections.split(",").map((s) => s.trim()).filter(Boolean) : [],
    };
    await api.patch(`/doctors/${detail._id}`, payload);
    toast.success("Doctor updated");
    const { data } = await api.get(`/doctors/${detail._id}`);
    setDetail(data); hydrate(data); load();
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${detail.name}? This cannot be undone.`)) return;
    await api.delete(`/doctors/${detail._id}`);
    toast.success("Doctor deleted");
    setDetail(null); setEdit(null); load();
  };

  const activeCount = items.filter((i) => i.stage === "active").length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-head text-ccc-navy" data-testid="doctors-title">Doctor Acquisition</h1>
          <p className="text-sm text-slate-500 mt-1">Demand-side originators · {activeCount} active / target {docTarget}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <DataPorter entity="doctors" onImported={load} />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-doctor-button" className="bg-ccc-blue hover:bg-ccc-blue2 text-white"><Plus className="h-4 w-4 mr-1.5" /> Add Doctor</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Add Doctor</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div className="col-span-2"><Label>Name</Label><Input data-testid="doctor-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Dr. ..." className="mt-1" /></div>
              <div><Label>Specialty</Label>
                <Select value={form.specialty} onValueChange={(v) => setForm({ ...form, specialty: v })}>
                  <SelectTrigger className="mt-1" data-testid="doctor-specialty-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SPECIALTIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>City</Label>
                <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })}>
                  <SelectTrigger className="mt-1" data-testid="doctor-city-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{INDIA_CITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Clinic / Hospital</Label><Input value={form.clinic} onChange={(e) => setForm({ ...form, clinic: e.target.value })} className="mt-1" /></div>
              <div><Label>Patient volume est.</Label><Input type="number" value={form.patient_volume_estimate} onChange={(e) => setForm({ ...form, patient_volume_estimate: e.target.value })} className="mt-1" /></div>
              <div><Label>Monthly order potential</Label><Input type="number" value={form.monthly_order_potential} onChange={(e) => setForm({ ...form, monthly_order_potential: e.target.value })} className="mt-1" /></div>
              <div><Label>Est. patients / month</Label><Input type="number" data-testid="doctor-est-patients-input" value={form.est_patients_per_month} onChange={(e) => setForm({ ...form, est_patients_per_month: e.target.value })} className="mt-1" /></div>
              <div><Label>Est. revenue / month (₹)</Label><Input type="number" data-testid="doctor-est-revenue-input" value={form.est_revenue_per_month} onChange={(e) => setForm({ ...form, est_revenue_per_month: e.target.value })} className="mt-1" /></div>
              <div><Label>Relationship owner</Label><Input value={form.relationship_owner} onChange={(e) => setForm({ ...form, relationship_owner: e.target.value })} className="mt-1" /></div>
              <div><Label>NMC 6.4.1 comfort</Label>
                <Select value={form.nmc_comfort} onValueChange={(v) => setForm({ ...form, nmc_comfort: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{["comfortable", "neutral", "concerned", "unknown"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>
            </div>
            <DialogFooter><Button data-testid="doctor-save-button" onClick={create} className="bg-ccc-blue hover:bg-ccc-blue2 text-white">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <ViewToggle view={view} setView={setView} />
      <div className="mt-4" />
      {view === "list" && (
        <FunnelList
          items={items}
          onRowClick={openDetail}
          testid="doctors-list"
          columns={[
            { label: "Name", sortKey: "name", render: (d) => <span className="font-medium text-ccc-navy">{d.name}</span> },
            { label: "Specialty", sortKey: "specialty", render: (d) => d.specialty || "—" },
            { label: "City", sortKey: "city", render: (d) => d.city || "—" },
            { label: "Clinic", sortKey: "clinic", render: (d) => d.clinic || "—" },
            { label: "Owner", sortKey: "relationship_owner", render: (d) => d.relationship_owner || "—" },
            { label: "Est. pts/mo", sortKey: "est_patients_per_month", render: (d) => d.est_patients_per_month ?? 0 },
            { label: "Est. rev/mo", sortKey: "est_revenue_per_month", render: (d) => inr(d.est_revenue_per_month || 0) },
            { label: "Stage", sortKey: "stage", render: (d) => <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px]">{labelOf(DOCTOR_STAGES, d.stage)}</Badge> },
          ]}
        />
      )}
      {view === "board" && (
      <KanbanBoard
        stages={DOCTOR_STAGES}
        items={items}
        accent="#D97706"
        onMove={move}
        onCardClick={openDetail}
        renderCard={(d) => (
          <div>
            <div className="flex items-start justify-between gap-2">
              <p className="font-semibold text-sm text-ccc-navy leading-tight">{d.name}</p>
              <Badge className={`${NMC[d.nmc_comfort]} border-0 text-[10px]`}>{d.nmc_comfort}</Badge>
            </div>
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Stethoscope className="h-3 w-3" />{d.specialty}</p>
            <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1"><Building2 className="h-3 w-3" />{d.clinic || "—"} · {d.city}</p>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[10px] font-medium bg-amber-50 text-ccc-amber px-2 py-0.5 rounded">{d.monthly_order_potential}/mo orders</span>
              {d.relationship_owner && <span className="text-[10px] text-slate-400">· {d.relationship_owner}</span>}
            </div>
          </div>
        )}
      />
      )}

      <Sheet open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setEdit(null); } }}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          {detail && edit && (
            <>
              <SheetHeader><SheetTitle className="font-head">Edit Doctor</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-4" data-testid="doctor-detail">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Name</Label><Input data-testid="edit-doctor-name" value={edit.name} onChange={(e) => setField("name", e.target.value)} className="mt-1" /></div>
                  <div><Label>Specialty</Label>
                    <Select value={edit.specialty || undefined} onValueChange={(v) => setField("specialty", v)}>
                      <SelectTrigger className="mt-1" data-testid="edit-doctor-specialty"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{SPECIALTIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>City</Label>
                    <Select value={edit.city || undefined} onValueChange={(v) => setField("city", v)}>
                      <SelectTrigger className="mt-1" data-testid="edit-doctor-city"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{INDIA_CITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Label>Clinic / Hospital</Label><Input value={edit.clinic} onChange={(e) => setField("clinic", e.target.value)} className="mt-1" /></div>
                  <div><Label>Patient volume est.</Label><Input type="number" value={edit.patient_volume_estimate} onChange={(e) => setField("patient_volume_estimate", e.target.value)} className="mt-1" /></div>
                  <div><Label>Monthly order potential</Label><Input type="number" value={edit.monthly_order_potential} onChange={(e) => setField("monthly_order_potential", e.target.value)} className="mt-1" /></div>
                  <div><Label>Est. patients / month</Label><Input type="number" data-testid="edit-doctor-est-patients" value={edit.est_patients_per_month} onChange={(e) => setField("est_patients_per_month", e.target.value)} className="mt-1" /></div>
                  <div><Label>Est. revenue / month (₹)</Label><Input type="number" data-testid="edit-doctor-est-revenue" value={edit.est_revenue_per_month} onChange={(e) => setField("est_revenue_per_month", e.target.value)} className="mt-1" /></div>
                  <div><Label>Relationship owner</Label><Input value={edit.relationship_owner} onChange={(e) => setField("relationship_owner", e.target.value)} className="mt-1" /></div>
                  <div><Label>NMC comfort</Label>
                    <Select value={edit.nmc_comfort} onValueChange={(v) => setField("nmc_comfort", v)}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{["comfortable", "neutral", "concerned", "unknown"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Stage</Label>
                    <Select value={edit.stage} onValueChange={(v) => setField("stage", v)}>
                      <SelectTrigger className="mt-1" data-testid="edit-doctor-stage"><SelectValue /></SelectTrigger>
                      <SelectContent>{DOCTOR_STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Last contact date</Label><Input type="date" value={edit.last_contact_date} onChange={(e) => setField("last_contact_date", e.target.value)} className="mt-1" /></div>
                  <div><Label>Onboarded date</Label><Input type="date" value={edit.onboarded_date} onChange={(e) => setField("onboarded_date", e.target.value)} className="mt-1" /></div>
                  <div className="col-span-2"><Label>Objections <span className="text-slate-400 font-normal">(comma-separated)</span></Label><Input value={edit.objections} onChange={(e) => setField("objections", e.target.value)} className="mt-1" /></div>
                  <div className="col-span-2"><Label>Notes</Label><Textarea value={edit.notes} onChange={(e) => setField("notes", e.target.value)} className="mt-1" /></div>
                </div>

                <Button data-testid="save-doctor-detail" onClick={saveEdit} className="w-full bg-ccc-blue hover:bg-ccc-blue2 text-white">Save changes</Button>
                {isAdmin && (
                  <Button data-testid="delete-doctor-button" variant="outline" onClick={remove} className="w-full border-rose-300 text-ccc-rose hover:bg-rose-50"><Trash2 className="h-4 w-4 mr-2" />Delete doctor</Button>
                )}

                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-testid="doctor-estimates">
                  <p className="text-xs font-semibold uppercase text-slate-400 mb-3">Estimates (per doctor)</p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <Roll label="Patients / mo" value={Number(edit.est_patients_per_month) || 0} />
                    <Roll label="Revenue / mo" value={inr(Number(edit.est_revenue_per_month) || 0)} color="#0D9488" />
                    <Roll label="Est. annual" value={inr((Number(edit.est_revenue_per_month) || 0) * 12)} color="#10B981" />
                  </div>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <p className="text-xs font-semibold uppercase text-slate-400 mb-3">Clinician Orders Roll-up</p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <Roll label="Clinician orders" value={detail.clinician_orders_count} />
                    <Roll label="Patients" value={detail.linked_patients_count} />
                    <Roll label="Enrolled" value={detail.enrolled_count} color="#10B981" />
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-center mt-3 pt-3 border-t border-slate-200" data-testid="doctor-revenue-rollup">
                    <Roll label="Booked revenue" value={inr(detail.expected_revenue || 0)} color="#2540FF" />
                    <Roll label="Collected" value={inr(detail.collected_revenue || 0)} color="#10B981" />
                  </div>
                  {detail.time_to_first_order_days != null && (
                    <p className="text-[11px] text-slate-400 mt-3 text-center">Time to first order: {detail.time_to_first_order_days} days</p>
                  )}
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase text-slate-400 mb-2">Linked Patients ({detail.linked_patients_count})</p>
                  <div className="space-y-1.5 max-h-56 overflow-y-auto">
                    {detail.linked_patients?.map((p) => (
                      <div key={p.id} className="flex items-center justify-between text-sm bg-white border border-slate-100 rounded-lg px-3 py-2">
                        <span className="text-slate-700">{p.name}</span>
                        <span className="text-xs text-slate-400">{p.stage}</span>
                      </div>
                    ))}
                    {detail.linked_patients_count === 0 && <p className="text-xs text-slate-400">No patients yet.</p>}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

const Roll = ({ label, value, color = "#0A1E3F" }) => (
  <div><p className="text-lg font-bold font-head" style={{ color }}>{value}</p><p className="text-[10px] text-slate-400">{label}</p></div>
);
